import React from "react"
import { Avatar, AvatarImage } from "../../../shared/components/ui/avatar"
import { AvatarFallback } from "@radix-ui/react-avatar"
import { Badge } from "../../../shared/components/ui/badge"
import { formatPhoneNumber, shortenName } from "@/shared/lib/utils"
import OwnerInfoMobile from "@/features/homeowners/components/owner-info-mobile"
import DeleteVolunteerButton from "./delete-volunteer-button"
import { formatDate } from "date-fns"
import { Volunteer, NonHomeownerUser, VolunteerTableRow } from "../types/volunteers.type"
import EditVolunteerButton from "./edit-volunteer-button"
import EditUserButton, { UpdateUserLink } from "./edit-user-button"
import DeleteUserButton from "./delete-user-button"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"

// Union type for mobile card data
type MobileCardDataItem = Volunteer | NonHomeownerUser | VolunteerTableRow

// Helper function to parse a date value (handles YYYY-MM-DD and ISO strings)
const parseDateValue = (dateValue: string | Date | undefined | null): Date | null => {
    if (!dateValue) return null
    
    if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue
    }
    
    if (typeof dateValue === 'string') {
        // If it's a date-only string (YYYY-MM-DD), parse as local time
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            const [year, month, day] = dateValue.split('-').map(Number)
            const date = new Date(year, month - 1, day)
            return isNaN(date.getTime()) ? null : date
        }
        // ISO string with time, parse normally
        const date = new Date(dateValue)
        return isNaN(date.getTime()) ? null : date
    }
    
    return null
}

// Helper function to find the appropriate serve period based on current date
// Priority: 1. Period containing today, 2. Next upcoming period, 3. Most recent past period
const findRelevantServePeriod = (serveDates: Array<{ from: string | Date; to: string | Date }> | null | undefined): { from: Date | null; to: Date | null } => {
    if (!serveDates || !Array.isArray(serveDates) || serveDates.length === 0) {
        return { from: null, to: null }
    }
    
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Normalize to midnight for comparison
    
    // Parse all periods
    const parsedPeriods = serveDates
        .map(period => {
            const from = parseDateValue(period.from)
            const to = parseDateValue(period.to)
            if (!from || !to) return null
            
            // Normalize dates to midnight for comparison
            const fromNormalized = new Date(from.getFullYear(), from.getMonth(), from.getDate())
            const toNormalized = new Date(to.getFullYear(), to.getMonth(), to.getDate())
            
            return { from: fromNormalized, to: toNormalized, originalFrom: from, originalTo: to }
        })
        .filter((period): period is NonNullable<typeof period> => period !== null)
        .sort((a, b) => a.from.getTime() - b.from.getTime()) // Sort by start date
    
    if (parsedPeriods.length === 0) {
        return { from: null, to: null }
    }
    
    // 1. Find period containing today
    const currentPeriod = parsedPeriods.find(period => 
        today >= period.from && today <= period.to
    )
    if (currentPeriod) {
        return { from: currentPeriod.originalFrom, to: currentPeriod.originalTo }
    }
    
    // 2. Find next upcoming period (starts after today)
    const upcomingPeriod = parsedPeriods.find(period => period.from > today)
    if (upcomingPeriod) {
        return { from: upcomingPeriod.originalFrom, to: upcomingPeriod.originalTo }
    }
    
    // 3. All periods are in the past, return the most recent one (last in sorted array)
    const mostRecentPeriod = parsedPeriods[parsedPeriods.length - 1]
    return { from: mostRecentPeriod.originalFrom, to: mostRecentPeriod.originalTo }
}

// Helper functions to extract data from both types
const getUserData = (item: MobileCardDataItem) => {
    return 'user' in item ? item.user : item
}

const getVolunteerData = (item: MobileCardDataItem) => {
    if ('user' in item) {
        // This is a Volunteer type
        return {
            status: item.status,
            locationServing: item.locationServing,
            arrivalDate: item.arrivalDate,
            departureDate: item.departureDate,
            organizationName: item.organizationName ?? null,
            serveDates: item.serveDates ?? null,
        }
    } else {
        // This is NonHomeownerUser or VolunteerTableRow (table rows lack status, etc.)
        const v = item.volunteer as { status?: boolean; locationServing?: string | null; arrivalDate?: string | null; departureDate?: string | null; organizationName?: string | null; serveDates?: Array<{ from: string; to: string }> | null } | undefined
        const rawServeDates = v?.serveDates
        const serveDates: Array<{ from: string | Date; to: string | Date }> | null =
            Array.isArray(rawServeDates) && rawServeDates.length > 0 ? rawServeDates : null
        return {
            status: v?.status ?? true,
            locationServing: v?.locationServing ?? null,
            arrivalDate: v?.arrivalDate ?? null,
            departureDate: v?.departureDate ?? null,
            organizationName: v?.organizationName ?? null,
            serveDates,
        }
    }
}

function VolunteerCardMobile({ item }: { item: MobileCardDataItem }) {
    const user = getUserData(item)
    const volunteerData = getVolunteerData(item)
    const { session } = useSession()
    const canEditDelete = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

    // Use serveDates to find the relevant period (normalize to expected type)
    const rawServeDates = volunteerData?.serveDates
    const serveDatesForPeriod: Array<{ from: string | Date; to: string | Date }> | null | undefined =
        Array.isArray(rawServeDates) && rawServeDates.length > 0 ? rawServeDates : (rawServeDates === undefined ? undefined : null)
    const period = findRelevantServePeriod(serveDatesForPeriod)
    const arrivalDate = period.from
    const departureDate = period.to
    const lastActiveDate = user.lastActiveAt ? parseDateValue(user.lastActiveAt) : null
    
    return (
        <div className="border p-3 space-y-4 rounded">
            <div className="flex justify-between gap-1">
                <div className="space-y-1">
                    <h3 className="text-muted-foreground text-xs">Name</h3>
                    <div className="flex gap-x-1">
                        <Avatar className="flex items-center justify-center bg-primary text-white w-9 h-9">
                            <AvatarImage
                                src={user.profilePicture ?? undefined}
                            />
                            <AvatarFallback>
                                {shortenName(user.fullname)}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col justify-center font-medium">
                            {canEditDelete ? (
                                <UpdateUserLink user={item}>
                                    <span className="text-sm">{user.fullname}</span>
                                </UpdateUserLink>
                            ) : (
                                <p className="text-foreground text-sm">{user.fullname}</p>
                            )}
                            <small className="text-muted-foreground text-[11px]">
                                {user.role.name}
                            </small>
                        </div>
                    </div>
                </div>
                <div className="space-y-1">
                    {volunteerData.status ? (
                        <Badge
                            variant={"default"}
                            className="bg-primary/15 text-foreground border-0 h-[22px] rounded-full flex items-center gap-2 justify-center w-fit font-medium"
                        >
                            <span className="w-1.5 h-1.5 aspect-square rounded-full bg-green-600"></span>
                            Active
                        </Badge>
                    ) : (
                        <Badge
                            variant={"outline"}
                            className="border-foreground rounded-full flex items-center h-[22px] gap-2 justify-center w-fit font-medium"
                        >
                            <span className="w-1.5 h-1.5 aspect-square rounded-full bg-foreground"></span>
                            Inactive
                        </Badge>
                    )}
                </div>
            </div>
            <div className="grid grid-cols-3 gap-y-5 gap-x-2">
                <OwnerInfoMobile
                    infoTitle="Organization"
                    infoValue={volunteerData.organizationName ?? "N/A"}
                />
                <OwnerInfoMobile
                    infoTitle="Role"
                    infoValue={user.role.name}
                />
                <OwnerInfoMobile
                    infoTitle="Phone Number"
                    infoValue={
                        user.phone
                            ? formatPhoneNumber(user.phone)
                            : "N/A"
                    }
                />
                <OwnerInfoMobile
                    infoTitle="Arrival Date"
                    infoValue={
                        arrivalDate
                            ? formatDate(arrivalDate, "dd-MMM-yyyy")
                            : "N/A"
                    }
                />
                <OwnerInfoMobile
                    infoTitle="Departure Date"
                    infoValue={
                        departureDate
                            ? formatDate(departureDate, "dd-MMM-yyyy")
                            : "N/A"
                    }
                />
                <OwnerInfoMobile
                    infoTitle="Last Active"
                    infoValue={lastActiveDate ? formatDate(lastActiveDate, "dd-MMM-yyyy") : "N/A"}
                />
            </div>
            {canEditDelete && (
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 ml-auto">
                        <EditUserButton user={item} />
                        <DeleteUserButton user={item} />
                    </div>
                </div>
            )}
        </div>
    )
}

export default VolunteerCardMobile
